import React, { useEffect, useRef } from "react";

const projects = [
  {
    id: "01",
    name: "dataguard_ai",
    type: "DATA QUALITY PLATFORM",
    description:
      "An intelligent data quality monitoring platform designed to detect, validate and surface issues across analytical datasets.",
    stack: ["Python", "FastAPI", "BigQuery", "Cloud Run", "AI"],
    accent: "#4CD8B8",
    icon: "devicon-googlecloud-plain",
    link: "https://github.com/Stephen0111",
  },
  {
    id: "02",
    name: "portfolio_assistant",
    type: "MULTI-AGENT AI SYSTEM",
    description:
      "A multi-agent RAG assistant combining retrieval, orchestration and LLM reasoning to create an intelligent interface over personal knowledge.",
    stack: ["Python", "Vertex AI", "LangGraph", "ChromaDB", "RAG"],
    accent: "#A78BFA",
    icon: "devicon-python-plain",
    link: "https://github.com/Stephen0111",
  },
];

function ProjectCard({ project }) {
  const cardRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMove = (event) => {
      const rect = card.getBoundingClientRect();

      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;

      card.style.setProperty("--rx", `${-y * 5}deg`);
      card.style.setProperty("--ry", `${x * 7}deg`);
      card.style.setProperty("--mx", `${(x + 0.5) * 100}%`);
      card.style.setProperty("--my", `${(y + 0.5) * 100}%`);
    };

    const handleLeave = () => {
      card.style.setProperty("--rx", "0deg");
      card.style.setProperty("--ry", "0deg");
      card.style.setProperty("--mx", "50%");
      card.style.setProperty("--my", "50%");
    };

    card.addEventListener("mousemove", handleMove);
    card.addEventListener("mouseleave", handleLeave);

    return () => {
      card.removeEventListener("mousemove", handleMove);
      card.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  return (
    <a
      ref={cardRef}
      href={project.link}
      target="_blank"
      rel="noreferrer"
      className="
        group relative block
        overflow-hidden
        rounded-3xl
        border border-white/10
        bg-[#0D1117]
        transition-all duration-500
        hover:border-white/20
      "
      style={{
        transform:
          "perspective(1200px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg))",
      }}
    >
      {/* Mouse-follow glow */}
      <div
        className="
          pointer-events-none absolute
          h-72 w-72
          -translate-x-1/2
          -translate-y-1/2
          rounded-full
          opacity-0
          blur-[100px]
          transition-opacity duration-500
          group-hover:opacity-20
        "
        style={{
          left: "var(--mx, 50%)",
          top: "var(--my, 50%)",
          backgroundColor: project.accent,
        }}
      />

      {/* Technical top bar */}
      <div className="relative flex items-center justify-between border-b border-white/5 px-6 py-4">
        <div className="flex items-center gap-3">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{
              backgroundColor: project.accent,
              boxShadow: `0 0 10px ${project.accent}`,
            }}
          />

          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/30">
            {project.type}
          </span>
        </div>

        <span className="font-mono text-[9px] text-white/15">
          PROJECT_{project.id}
        </span>
      </div>

      {/* Main project area */}
      <div className="relative grid gap-8 p-6 md:grid-cols-[0.85fr_1.15fr] md:p-8">
        {/* Visual */}
        <div
          className="
            relative
            flex min-h-[230px]
            items-center justify-center
            overflow-hidden
            rounded-2xl
            border border-white/10
            bg-[#080B10]
          "
        >
          {/* Grid */}
          <div
            className="
              absolute inset-0 opacity-[0.12]
              [background-image:linear-gradient(rgba(255,255,255,.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.1)_1px,transparent_1px)]
              [background-size:32px_32px]
            "
          />

          {/* Glow */}
          <div
            className="absolute h-36 w-36 rounded-full blur-[70px]"
            style={{
              backgroundColor: project.accent,
              opacity: 0.12,
            }}
          />

          {/* Central icon */}
          <div
            className="
              relative z-10
              flex h-24 w-24
              items-center justify-center
              rounded-3xl
              border
              bg-black/40
              transition-all duration-700
              group-hover:scale-110
            "
            style={{
              borderColor: `${project.accent}45`,
              boxShadow: `0 0 50px ${project.accent}15`,
            }}
          >
            <i
              className={`${project.icon} text-[3.2rem]`}
              style={{
                color: project.accent,
                filter: `drop-shadow(0 0 12px ${project.accent}45)`,
              }}
            />
          </div>

          {/* Orbit */}
          <div
            className="
              absolute h-40 w-40
              rounded-full
              border
              border-dashed
              opacity-30
              transition-transform duration-1000
              group-hover:rotate-180
            "
            style={{
              borderColor: project.accent,
            }}
          />

          <div className="absolute bottom-4 left-4 font-mono text-[8px] uppercase tracking-[0.2em] text-white/20">
            system / online
          </div>

          <div
            className="absolute right-4 top-4 font-mono text-[8px]"
            style={{
              color: `${project.accent}90`,
            }}
          >
            ●
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col justify-center">
          <div
            className="font-mono text-[10px] uppercase tracking-[0.2em]"
            style={{
              color: project.accent,
            }}
          >
            {project.type}
          </div>

          <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white md:text-3xl">
            {project.name}
          </h2>

          <p className="mt-5 max-w-xl text-sm leading-7 text-white/40">
            {project.description}
          </p>

          {/* Stack */}
          <div className="mt-7 flex flex-wrap gap-2">
            {project.stack.map((technology) => (
              <span
                key={technology}
                className="
                  rounded-full
                  border border-white/10
                  bg-white/[0.025]
                  px-3 py-1.5
                  font-mono
                  text-[8px]
                  uppercase
                  tracking-[0.14em]
                  text-white/35
                  transition-colors
                  group-hover:border-white/15
                "
              >
                {technology}
              </span>
            ))}
          </div>

          {/* GitHub indicator */}
          <div className="mt-8 flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.18em] text-white/25 transition-colors group-hover:text-white/50">
            <i className="devicon-github-original text-base text-white/50" />
            View source
            <span className="transition-transform duration-300 group-hover:translate-x-1">
              →
            </span>
          </div>
        </div>
      </div>

      {/* Bottom signal */}
      <div className="relative h-[2px] w-full overflow-hidden bg-white/5">
        <div
          className="
            absolute left-0 top-0
            h-full w-1/3
            -translate-x-full
            transition-transform duration-1000
            group-hover:translate-x-[400%]
          "
          style={{
            backgroundColor: project.accent,
          }}
        />
      </div>
    </a>
  );
}

export default function Work() {
  return (
    <main className="min-h-screen bg-[#080B10] text-[#EDEFF2]">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute left-[15%] top-[10%] h-80 w-80 rounded-full bg-[#4CD8B8]/5 blur-[130px]" />

        <div className="absolute right-[10%] top-[45%] h-96 w-96 rounded-full bg-[#A78BFA]/5 blur-[150px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-10">
        {/* Header */}
        <section className="max-w-3xl">
          <div className="mb-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#4CD8B8]">
            <span className="h-px w-8 bg-[#4CD8B8]" />
            selected systems
          </div>

          <h1 className="text-5xl font-semibold leading-[0.95] tracking-[-0.045em] text-white sm:text-6xl">
            Things I've
            <br />
            <span className="text-white/25">built.</span>
          </h1>

          <p className="mt-7 max-w-2xl text-base leading-8 text-white/40">
            A small selection of engineering projects exploring data platforms,
            cloud infrastructure and intelligent systems.
          </p>
        </section>

        {/* Projects */}
        <section className="mt-14 space-y-5">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </section>

        {/* GitHub footer */}
        <section className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/25">
                more source
              </div>

              <p className="mt-2 text-sm text-white/40">
                More experiments, projects and engineering work live on GitHub.
              </p>
            </div>

            <a
              href="https://github.com/Stephen0111"
              target="_blank"
              rel="noreferrer"
              className="
                inline-flex items-center gap-3
                rounded-xl
                border border-white/10
                bg-white/[0.03]
                px-5 py-3
                font-mono
                text-[10px]
                uppercase
                tracking-[0.15em]
                text-white/50
                transition-all duration-300
                hover:border-[#4CD8B8]/30
                hover:bg-[#4CD8B8]/5
                hover:text-[#4CD8B8]
              "
            >
              <i className="devicon-github-original text-base" />
              GitHub
              <span>↗</span>
            </a>
          </div>
        </section>

        {/* Footer signal */}
        <div className="mt-16 flex items-center justify-between border-t border-white/10 pt-5 font-mono text-[9px] uppercase tracking-[0.2em] text-white/20">
          <span>work / selected systems</span>

          <span className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#4CD8B8]" />
            source available
          </span>
        </div>
      </div>
    </main>
  );
}
